import {
  CircularProgress,
  MenuItem,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import {
  fetchDailyAverageDanjiPrice,
  fetchMonthlyAverageDanjiPrice,
} from 'api/chart';

import EmptyData from 'components/EmptyData';
import { SwitchCase } from 'components/SwitchCase';
import ChartToggleButtonGroup from 'components/buttonGroup/ChartToggleButtonGroup';
import CompareOfferBarChart from 'components/chart/CompareOfferBarChart';
import CompareOfferLineChart from 'components/chart/CompareOfferLineChart';
import ChartSelect from 'components/select/ChartSelect';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useLocation } from 'react-router-dom';
import { QUOTE_CHART_DATE_SELECT_FILTER } from 'static/chart';
import styled from 'styled-components';
import {
  ChartOption,
  CompareOfferBarChartData,
  CompareOfferLineChartData,
  DateFilter,
  OfferTransferChartFilter,
  PriceChartParams,
  TradeType,
} from 'types/chart';
import {
  filterZeroAndGetMax,
  filterZeroAndGetMin,
  getDateRangeForFilter,
  getIsVisibleDay,
} from 'utils/chart';
import {
  chartYtickFormatter,
  generateDayArray,
  generateMonthArray,
} from 'utils/formatUtil';

const INIT_START_DATE = dayjs().subtract(3, 'year').format('YYYY-MM-DD');
const INIT_END_DATE = dayjs().subtract(1, 'day').format('YYYY-MM-DD');

const getBarChartEmptyText = ({
  sellingEmpty,
  wantBuyingEmpty,
}: ChartEmpty) => {
  if (sellingEmpty && wantBuyingEmpty) {
    return '해당 단지들에 대한 데이터가 없습니다.';
  }

  if (sellingEmpty) {
    return '팔려는 아파트의 평형 대에 대한 데이터가 없습니다.';
  }

  if (wantBuyingEmpty) {
    return '사려는 아파트의 평형 대에 대한 데이터가 없습니다.';
  }

  return '';
};

interface ChartEmpty {
  sellingEmpty: boolean;
  wantBuyingEmpty: boolean;
}

/**
 * 갈아타기 페이지 차트
 */
function OfferTransferChartPage() {
  const location = useLocation();
  const { search } = location;
  const urlParameter = new URLSearchParams(search);

  const urlSellingComplexId = urlParameter.get('sellingComplexId');
  const urlSellingAreaInPyeong = urlParameter.get('sellingAreaInPyeong');
  const urlSellingTradeType = urlParameter.get('sellingTradeType');
  const urlWantBuyingComplexId = urlParameter.get('wantBuyingComplexId');
  const urlWantBuyingAreaInPyeong = urlParameter.get('wantBuyingAreaInPyeong');
  const urlWantBuyingTradeType = urlParameter.get('wantBuyingTradeType');

  const [chartCompareLineData, setChartCompareLineData] = useState<
    CompareOfferLineChartData[]
  >([]);
  const [chartCompareBarData, setChartCompareBarData] = useState<
    CompareOfferBarChartData[]
  >([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [chartEmpty, setChartEmpty] = useState<ChartEmpty>({
    sellingEmpty: false,
    wantBuyingEmpty: false,
  });
  const [chartKey, setChartKey] = useState('');
  const [compareOfferLineChartOption, setCompareOfferLineChartOption] =
    useState<ChartOption>({ tooltipActive: false });
  const [compareOfferBarChartOption, setCompareOfferBarChartOption] =
    useState<ChartOption>({
      tooltipActive: false,
    });

  const [params, setParams] = useState<
    Omit<PriceChartParams, 'areaInPyeong' | 'complexId'>
  >({
    startDate: INIT_START_DATE,
    endDate: INIT_END_DATE,
    tradeType: 'TRADE',
  });
  const [sellingParams, setSellingParams] = useState<
    Pick<PriceChartParams, 'areaInPyeong' | 'complexId' | 'tradeType'>
  >({
    complexId: undefined,
    areaInPyeong: undefined,
    tradeType: undefined,
  });

  const [wantBuyingParams, setWantBuyingParams] = useState<
    Pick<PriceChartParams, 'areaInPyeong' | 'complexId' | 'tradeType'>
  >({
    complexId: undefined,
    areaInPyeong: undefined,
    tradeType: undefined,
  });

  const [isCompareButton, setIsCompareButton] = useState<boolean | undefined>(
    undefined,
  );
  const [newChartType, setNewChartType] = useState();
  const [compareTextType, setCompareTextType] = useState();
  const [initialChartType, setInitialChartType] = useState('COMPARE');

  const isSellingParamsEmpty =
    !sellingParams.complexId && !sellingParams.areaInPyeong;
  const isWantBuyingParamsEmpty =
    !wantBuyingParams.complexId && !wantBuyingParams.areaInPyeong;

  const [isVisibleDay, setIsVisibleDay] = useState(false);

  const [filter, setFilter] = useState<OfferTransferChartFilter>({
    dateFilter: 'THREE_YEAR',
    chartType: 'DIFFERENCE',
  });
  const { dateFilter, chartType } = filter;

  const fetchData = async (isVisible: boolean) => {
    setChartLoading(true);

    const resultSellingParam: PriceChartParams = {
      ...params,
      ...sellingParams,
    };
    const resultWantBuyingParam: PriceChartParams = {
      ...params,
      ...wantBuyingParams,
    };
    const responseDailyDanjiAveragePriceSellingOffer =
      await fetchDailyAverageDanjiPrice(resultSellingParam);
    const responseDailyDanjiAveragePriceWantBuyingOffer =
      await fetchDailyAverageDanjiPrice(resultWantBuyingParam);
    const responseMonthlyDanjiAveragePriceSellingOffer =
      await fetchMonthlyAverageDanjiPrice(resultSellingParam);
    const responseMonthlyDanjiAveragePriceWantBuyingOffer =
      await fetchMonthlyAverageDanjiPrice(resultWantBuyingParam);

    const sellingEmpty = isVisible
      ? responseDailyDanjiAveragePriceSellingOffer.data?.data.length === 0
      : responseMonthlyDanjiAveragePriceSellingOffer.data?.data.length === 0;
    const wantBuyingEmpty = isVisible
      ? responseDailyDanjiAveragePriceWantBuyingOffer.data?.data.length === 0
      : responseMonthlyDanjiAveragePriceWantBuyingOffer.data?.data.length === 0;
    const barEmpty = sellingEmpty || wantBuyingEmpty; // 판매 - 구매 둘 중 하나 없을경우 막대 그리기 불가

    // 차트 데이터 유무 표기
    setChartEmpty((prev) => ({
      ...prev,
      sellingEmpty,
      wantBuyingEmpty,
      barEmpty,
    }));

    const sellingPricesDaily =
      responseDailyDanjiAveragePriceSellingOffer.data?.data
        .map((value) => value.averagePrice)
        .filter((price): price is number => price !== null);
    const wantBuyingPricesDaily =
      responseDailyDanjiAveragePriceWantBuyingOffer.data?.data
        .map((value) => value.averagePrice)
        .filter((price): price is number => price !== null);
    const sellingPricesMonthly =
      responseMonthlyDanjiAveragePriceSellingOffer.data?.data
        .map((value) => value.averagePrice)
        .filter((price): price is number => price !== null);
    const wantBuyingPricesMonthly =
      responseMonthlyDanjiAveragePriceWantBuyingOffer.data?.data
        .map((value) => value.averagePrice)
        .filter((price): price is number => price !== null);

    const minSellingPriceDaily = sellingPricesDaily?.length
      ? filterZeroAndGetMin(sellingPricesDaily)
      : 0;
    const maxSellingPriceDaily = sellingPricesDaily?.length
      ? filterZeroAndGetMax(sellingPricesDaily)
      : 0;
    const minWantBuyingPriceDaily = wantBuyingPricesDaily?.length
      ? filterZeroAndGetMin(wantBuyingPricesDaily)
      : 0;
    const maxWantBuyingPriceDaily = wantBuyingPricesDaily?.length
      ? filterZeroAndGetMax(wantBuyingPricesDaily)
      : 0;
    const minSellingPriceMonthly = sellingPricesMonthly?.length
      ? filterZeroAndGetMin(sellingPricesMonthly)
      : 0;
    const maxSellingPriceMonthly = sellingPricesMonthly?.length
      ? filterZeroAndGetMax(sellingPricesMonthly)
      : 0;
    const minWantBuyingPriceMonthly = wantBuyingPricesMonthly?.length
      ? filterZeroAndGetMin(wantBuyingPricesMonthly)
      : 0;
    const maxWantBuyingPriceMonthly = wantBuyingPricesMonthly?.length
      ? filterZeroAndGetMax(wantBuyingPricesMonthly)
      : 0;

    const minDaily = filterZeroAndGetMin([
      minSellingPriceDaily,
      minWantBuyingPriceDaily,
    ]);
    const maxDaily = filterZeroAndGetMax([
      maxSellingPriceDaily,
      maxWantBuyingPriceDaily,
    ]);
    const minMonthly = filterZeroAndGetMin([
      minSellingPriceMonthly,
      minWantBuyingPriceMonthly,
    ]);
    const maxMonthly = filterZeroAndGetMax([
      maxSellingPriceMonthly,
      maxWantBuyingPriceMonthly,
    ]);

    const tickCount = 5;
    const numbers = [
      100, 300, 500, 1000, 1500, 3000, 5000, 10000, 20000, 30000, 50000,
    ];
    const { ticks: ticksDaily, domain: domainDaily } = chartYtickFormatter({
      min: minDaily,
      max: maxDaily,
      tickCount,
      numbers,
    });
    const { ticks: ticksMonthly, domain: domainMonthly } = chartYtickFormatter({
      min: minMonthly,
      max: maxMonthly,
      tickCount,
      numbers,
    });

    const domain = isVisible ? domainDaily : domainMonthly;
    const ticks = isVisible ? ticksDaily : ticksMonthly;

    setCompareOfferLineChartOption((prev) => ({
      ...prev,
      yAxisProps: {
        tickCount,
        domain,
        ticks,
      },
    }));

    const { startDate, endDate } = resultSellingParam;
    const dayArray = generateDayArray(startDate, endDate, 'YYYY-MM-DD');

    const monthArray = generateMonthArray(startDate, endDate, 'YYYY-MM');

    const dateArray = isVisible ? dayArray : monthArray;

    const filterDailyDanjiAveragePriceSellingOffer = (date: string) =>
      responseDailyDanjiAveragePriceSellingOffer.data?.data.filter((value) => {
        return value.createDate === date;
      }) || [];
    const filterDailyDanjiAveragePriceWantBuyingOffer = (date: string) =>
      responseDailyDanjiAveragePriceWantBuyingOffer.data?.data.filter(
        (value) => {
          return value.createDate === date;
        },
      ) || [];
    const filterMonthlyDanjiAveragePriceSellingOffer = (date: string) =>
      responseMonthlyDanjiAveragePriceSellingOffer.data?.data.filter(
        (value) => {
          return value.yearMonth === date;
        },
      ) || [];
    const filterMonthlyDanjiAveragePriceWantBuyingOffer = (date: string) =>
      responseMonthlyDanjiAveragePriceWantBuyingOffer.data?.data.filter(
        (value) => {
          return value.yearMonth === date;
        },
      ) || [];

    const compareOfferLineChartData: CompareOfferLineChartData[] =
      dateArray.map((value) => {
        const dailyDanjiAveragePriceSellingOfferData =
          filterDailyDanjiAveragePriceSellingOffer(value);
        const dailyDanjiAveragePriceBuyingOfferData =
          filterDailyDanjiAveragePriceWantBuyingOffer(value);
        const monthlyDanjiAveragePriceSellingOfferData =
          filterMonthlyDanjiAveragePriceSellingOffer(value);
        const monthlyDanjiAveragePriceBuyingOfferData =
          filterMonthlyDanjiAveragePriceWantBuyingOffer(value);

        const sellingOfferDanjiAveragePrice = isVisible
          ? Number(
              dailyDanjiAveragePriceSellingOfferData
                .map((value) => value.averagePrice)
                .join(''),
            )
          : Number(
              monthlyDanjiAveragePriceSellingOfferData
                .map((value) => value.averagePrice)
                .join(''),
            );
        const wantBuyingOfferDanjiAveragePrice = isVisible
          ? Number(
              dailyDanjiAveragePriceBuyingOfferData
                .map((value) => value.averagePrice)
                .join(''),
            )
          : Number(
              monthlyDanjiAveragePriceBuyingOfferData
                .map((value) => value.averagePrice)
                .join(''),
            );

        if (
          sellingOfferDanjiAveragePrice === 0 &&
          wantBuyingOfferDanjiAveragePrice === 0
        )
          return {
            date: value,
          };

        if (sellingOfferDanjiAveragePrice === 0)
          return {
            date: value,
            wantBuyingOfferDanjiAveragePrice,
          };
        if (wantBuyingOfferDanjiAveragePrice === 0)
          return {
            date: value,
            sellingOfferDanjiAveragePrice,
          };

        return {
          date: value,
          sellingOfferDanjiAveragePrice,
          wantBuyingOfferDanjiAveragePrice,
        };
      });

    const compareOfferBarChartData: CompareOfferBarChartData[] = dateArray.map(
      (value, index) => {
        const dailyDanjiAveragePriceSellingOfferData =
          filterDailyDanjiAveragePriceSellingOffer(value);
        const dailyDanjiAveragePriceBuyingOfferData =
          filterDailyDanjiAveragePriceWantBuyingOffer(value);
        const monthlyDanjiAveragePriceSellingOfferData =
          filterMonthlyDanjiAveragePriceSellingOffer(value);
        const monthlyDanjiAveragePriceBuyingOfferData =
          filterMonthlyDanjiAveragePriceWantBuyingOffer(value);

        const sellingOfferDanjiAveragePrice = isVisible
          ? Number(
              dailyDanjiAveragePriceSellingOfferData
                .map((value) => value.averagePrice)
                .join(''),
            )
          : Number(
              monthlyDanjiAveragePriceSellingOfferData
                .map((value) => value.averagePrice)
                .join(''),
            );
        const wantBuyingOfferDanjiAveragePrice = isVisible
          ? Number(
              dailyDanjiAveragePriceBuyingOfferData
                .map((value) => value.averagePrice)
                .join(''),
            )
          : Number(
              monthlyDanjiAveragePriceBuyingOfferData
                .map((value) => value.averagePrice)
                .join(''),
            );

        const difference =
          sellingOfferDanjiAveragePrice - wantBuyingOfferDanjiAveragePrice;

        return {
          date: value,
          difference,
        };
      },
    );

    setChartCompareLineData(compareOfferLineChartData);
    setChartCompareBarData(compareOfferBarChartData);

    setChartLoading(false);

    const lineXInterval = isVisible
      ? Math.ceil(compareOfferLineChartData.length / 6)
      : 'preserveEnd';
    const barXInterval = isVisible
      ? Math.ceil(compareOfferBarChartData.length / 6)
      : 'preserveEnd';

    setCompareOfferLineChartOption((prev) => ({
      ...prev,
      xAxisProps: {
        ...prev.xAxisProps,
        interval: lineXInterval,
      },
    }));
    setCompareOfferBarChartOption((prev) => ({
      ...prev,
      xAxisProps: {
        ...prev.xAxisProps,
        interval: barXInterval,
      },
    }));
  };

  const handleDateFilter = (event: SelectChangeEvent<unknown>) => {
    const filter = event.target.value as DateFilter;
    const date = getDateRangeForFilter(filter);
    const isVisible = getIsVisibleDay(date);

    setParams((prev) => ({
      ...prev,
      ...date,
    }));
    setIsVisibleDay(isVisible);
    setFilter((prev) => ({
      ...prev,
      dateFilter: filter,
    }));
  };

  const handleChartTypeFilter = (type: 'DIFFERENCE' | 'COMPARE') => {
    setFilter((prev) => ({
      ...prev,
      chartType: type,
    }));
  };

  const initWebViewMessageListener = async () => {
    if (!isMobile) {
      return;
    }

    if (!window.ReactNativeWebView) {
      return;
    }

    const webviewListener = (event: any) => {
      try {
        const { payload, type } = JSON.parse(event.data);
        switch (type) {
          case 'setComparingComplexes':
            setSellingParams((prev) => ({
              ...prev,
              complexId: payload.sellingParams.complexId,
              areaInPyeong: payload.sellingParams.areaInPyeong,
              tradeType: payload.sellingParams.tradeType,
            }));

            setWantBuyingParams((prev) => ({
              ...prev,
              complexId: payload.wantBuyingParams.complexId,
              areaInPyeong: payload.wantBuyingParams.areaInPyeong,
              tradeType: payload.wantBuyingParams.tradeType,
            }));

            setIsCompareButton(
              payload.sellingParams.tradeType ===
                payload.wantBuyingParams.tradeType,
            );

            setNewChartType(payload.chartType.chartType);

            setCompareTextType(payload.pageType.pageType);
            break;
          default:
            break;
        }
      } catch (error) {
        console.log('json pares error', error);
      }
    };

    document.addEventListener('message', webviewListener);
    window.addEventListener('message', webviewListener);
  };

  const _renderChart = () => {
    if (isWantBuyingParamsEmpty) {
      return (
        <EmptyData
          minHeight="200px"
          text="사려는 아파트 단지를 선택해 주세요"
        />
      );
    }

    if (chartLoading) return <CircularProgress sx={{ color: '#9E9E9E' }} />;

    if (!chartLoading)
      return (
        <SwitchCase
          value={initialChartType}
          caseBy={
            isCompareButton
              ? {
                  DIFFERENCE: (
                    <>
                      {compareTextType ? null : (
                        <>
                          {chartEmpty.sellingEmpty ||
                          chartEmpty.wantBuyingEmpty ? (
                            <EmptyData
                              minHeight="200px"
                              text={getBarChartEmptyText(chartEmpty)}
                            />
                          ) : (
                            <CompareOfferBarChart
                              data={chartCompareBarData}
                              chartOption={compareOfferBarChartOption}
                              chartKey={chartKey}
                              isVisibleDay={isVisibleDay}
                            />
                          )}
                        </>
                      )}
                    </>
                  ),
                  COMPARE: (
                    <>
                      {chartEmpty.sellingEmpty && chartEmpty.wantBuyingEmpty ? (
                        <EmptyData
                          minHeight="200px"
                          text={getBarChartEmptyText(chartEmpty)}
                        />
                      ) : (
                        <CompareOfferLineChart
                          data={chartCompareLineData}
                          chartOption={compareOfferLineChartOption}
                          chartKey={chartKey}
                          isVisibleDay={isVisibleDay}
                          isCompareButton={isCompareButton}
                          newChartType={newChartType}
                          compareTextType={compareTextType}
                        />
                      )}
                    </>
                  ),
                }
              : {
                  COMPARE: (
                    <>
                      {chartEmpty.sellingEmpty && chartEmpty.wantBuyingEmpty ? (
                        <EmptyData
                          minHeight="200px"
                          text={getBarChartEmptyText(chartEmpty)}
                        />
                      ) : (
                        <CompareOfferLineChart
                          data={chartCompareLineData}
                          chartOption={compareOfferLineChartOption}
                          chartKey={chartKey}
                          isVisibleDay={isVisibleDay}
                          isCompareButton={isCompareButton}
                          newChartType={newChartType}
                          compareTextType={compareTextType}
                        />
                      )}
                    </>
                  ),
                }
          }
        />
      );
    return <></>;
  };

  useEffect(() => {
    if (compareTextType) {
      setInitialChartType('COMPARE');
    } else if (isCompareButton) {
      setInitialChartType(chartType);
    } else {
      setInitialChartType('COMPARE');
    }
  }, [compareTextType, isCompareButton, chartType]);

  useEffect(() => {
    initWebViewMessageListener();
  }, []);

  useEffect(() => {
    const visibleDay = getIsVisibleDay({
      startDate: params.startDate,
      endDate: params.endDate,
    });

    if (
      sellingParams.areaInPyeong &&
      sellingParams.complexId &&
      sellingParams.tradeType &&
      wantBuyingParams.areaInPyeong &&
      wantBuyingParams.complexId &&
      wantBuyingParams.tradeType
    ) {
      fetchData(visibleDay);
    }
    setChartKey(
      `${params.startDate}-${params.endDate}-${params.tradeType}-${sellingParams.complexId}-${wantBuyingParams.complexId}-${visibleDay}`,
    );
  }, [sellingParams, wantBuyingParams, params]);

  useEffect(() => {
    const isAllUrlParams =
      urlSellingAreaInPyeong &&
      urlSellingComplexId &&
      urlWantBuyingAreaInPyeong &&
      urlWantBuyingComplexId;

    if (isAllUrlParams) {
      setSellingParams((prevParams) => ({
        ...prevParams,
        complexId: Number(urlSellingComplexId),
        areaInPyeong: Number(urlSellingAreaInPyeong),
        tradeType: urlSellingTradeType as TradeType,
      }));
      setWantBuyingParams((prevParams) => ({
        ...prevParams,
        complexId: Number(urlWantBuyingComplexId),
        areaInPyeong: Number(urlWantBuyingAreaInPyeong),
        tradeType: urlWantBuyingTradeType as TradeType,
      }));
    }
  }, [search]);

  return (
    <Stack gap="13px">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <ChartToggleButtonGroup
          value={chartType}
          onChange={handleChartTypeFilter}
          buttons={
            isCompareButton && !compareTextType
              ? [
                  {
                    value: 'DIFFERENCE',
                    label: '차액',
                  },
                  {
                    value: 'COMPARE',
                    label: '비교',
                  },
                ]
              : [
                  {
                    value: 'COMPARE',
                    label: '비교',
                  },
                ]
          }
        />
        <ChartSelect value={dateFilter} onChange={handleDateFilter}>
          {QUOTE_CHART_DATE_SELECT_FILTER.map(({ value, label }) => (
            <CustomMenuItem value={value} key={value}>
              {label}
            </CustomMenuItem>
          ))}
        </ChartSelect>
      </Stack>

      <Stack
        width="100%"
        height={200}
        alignItems="center"
        justifyContent="center"
      >
        {_renderChart()}
      </Stack>
    </Stack>
  );
}

export default OfferTransferChartPage;

const CustomMenuItem = styled(MenuItem)`
  &.MuiMenuItem-root {
    font-size: 12px;
    padding-top: 0;
    padding-bottom: 0;
    color: #777777;
    font-weight: 700;
    &.Mui-selected {
      color: #fff;
      background: #333;
      &:hover {
        background: #333;
      }
      &:focus-visible {
        background: #333;
      }
    }
  }
`;
